import { Extension } from '@tiptap/core';

export interface IndentOptions {
  types: string[];
  minLevel: number;
  maxLevel: number;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
      indentListItem: () => ReturnType;
      outdentListItem: () => ReturnType;
    };
  }
}

// Helper function to check if the selection is inside a list
function isInList(state: any): boolean {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === 'bulletList' || node.type.name === 'orderedList') {
      return true;
    }
  }
  return false;
}

export const Indent = Extension.create<IndentOptions>({
  name: 'indent',

  addOptions() {
    return {
      types: ['paragraph', 'heading'],
      minLevel: 0,
      maxLevel: 4,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: element => {
              const indent = element.getAttribute('data-indent');
              return indent ? parseInt(indent, 10) : 0;
            },
            renderHTML: attributes => {
              if (!attributes.indent || attributes.indent === 0) {
                return {};
              }
              return {
                'data-indent': attributes.indent,
                style: `text-indent: ${attributes.indent * 2}em`,
              };
            },
          },
        },
      },
      {
        types: ['listItem'],
        attributes: {
          indent: {
            default: 0,
            parseHTML: element => {
              const indent = element.getAttribute('data-indent');
              return indent ? parseInt(indent, 10) : 0;
            },
            renderHTML: attributes => {
              if (!attributes.indent || attributes.indent === 0) {
                return {};
              }
              return {
                'data-indent': attributes.indent,
                style: `margin-left: ${attributes.indent * 2}em`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent:
        () =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          const { from, to } = selection;
          let changed = false;

          state.doc.nodesBetween(from, to, (node, pos) => {
            if (this.options.types.includes(node.type.name)) {
              const currentIndent = node.attrs.indent || 0;
              if (currentIndent < this.options.maxLevel) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent: currentIndent + 1,
                });
                changed = true;
              }
            }
          });

          if (changed && dispatch) {
            dispatch(tr);
          }

          return changed;
        },
      outdent:
        () =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          const { from, to } = selection;
          let changed = false;

          state.doc.nodesBetween(from, to, (node, pos) => {
            if (this.options.types.includes(node.type.name)) {
              const currentIndent = node.attrs.indent || 0;
              if (currentIndent > this.options.minLevel) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent: currentIndent - 1,
                });
                changed = true;
              }
            }
          });

          if (changed && dispatch) {
            dispatch(tr);
          }

          return changed;
        },
      indentListItem:
        () =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          const { $from } = selection;
          let changed = false;

          // Find the listItem node
          for (let depth = $from.depth; depth > 0; depth--) {
            const node = $from.node(depth);
            if (node.type.name === 'listItem') {
              const pos = $from.before(depth);
              const currentIndent = node.attrs.indent || 0;
              if (currentIndent < this.options.maxLevel) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent: currentIndent + 1,
                });
                changed = true;
              }
              break;
            }
          }

          if (changed && dispatch) {
            dispatch(tr);
          }

          return changed;
        },
      outdentListItem:
        () =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          const { $from } = selection;
          let changed = false;

          // Find the listItem node
          for (let depth = $from.depth; depth > 0; depth--) {
            const node = $from.node(depth);
            if (node.type.name === 'listItem') {
              const pos = $from.before(depth);
              const currentIndent = node.attrs.indent || 0;
              if (currentIndent > this.options.minLevel) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent: currentIndent - 1,
                });
                changed = true;
              }
              break;
            }
          }

          if (changed && dispatch) {
            dispatch(tr);
          }

          return changed;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        // If in a list, try sinkListItem first, then fall back to margin indent
        if (isInList(this.editor.state)) {
          const sunk = this.editor.commands.sinkListItem('listItem');
          if (sunk) return true;
          // If sinkListItem failed (no previous sibling), use margin indent
          return this.editor.commands.indentListItem();
        }
        // Otherwise, apply first-line text indent
        return this.editor.commands.indent();
      },
      'Shift-Tab': () => {
        // If in a list, first try to remove margin indent, then lift
        if (isInList(this.editor.state)) {
          // First check if there's margin indent to remove
          const { $from } = this.editor.state.selection;
          for (let depth = $from.depth; depth > 0; depth--) {
            const node = $from.node(depth);
            if (node.type.name === 'listItem' && (node.attrs.indent || 0) > 0) {
              return this.editor.commands.outdentListItem();
            }
          }
          // No margin indent, try liftListItem
          return this.editor.commands.liftListItem('listItem');
        }
        // Otherwise, remove first-line text indent
        return this.editor.commands.outdent();
      },
      Backspace: ({ editor }) => {
        const { selection } = editor.state;
        const { $from } = selection;
        
        // Only handle if cursor is at the start of a text block (no selection, at position 0)
        if (!selection.empty) return false;
        
        // Check if we're at the beginning of a paragraph/heading
        const isAtStart = $from.parentOffset === 0;
        if (!isAtStart) return false;
        
        // Check if the current node has an indent
        const node = $from.parent;
        const currentIndent = node.attrs.indent || 0;
        
        if (currentIndent > 0 && this.options.types.includes(node.type.name)) {
          // Remove one level of indentation
          return editor.commands.outdent();
        }
        
        return false;
      },
    };
  },
});
